import React from 'react';

import AppButton from '../buttons/AppButton';
import AppCheckbox from '../checkbox/AppCheckbox';
import AppSelect from '../select/AppSelect';

import { groupRows } from './fieldModel';

import styles from './processFlow.module.scss';

import { ICONS } from '@/constants/icons';

// I tipi che rendono un controllo con un `id` proprio: solo per loro
// `<label for>` punta a qualcosa. Gli altri (scelta rapida, gruppo, foto) sono
// insiemi di controlli, e si etichettano con `aria-labelledby`.
const LABELABLE = new Set(['text', 'number', 'date', 'textarea', 'select', 'multiselect']);

/**
 * Un campo di una pratica, qualunque sia il suo tipo.
 *
 * Definition-driven: aggiungere un tipo qui è l'unico punto da toccare perché
 * compaia ovunque una pratica si compili. Non conosce alcun workflow — se
 * servisse uno `switch` su uno slug, l'astrazione avrebbe una falla.
 *
 * `compact` è la variante dentro una riga di gruppo: l'etichetta la porta già
 * l'intestazione della colonna, quindi qui resta solo per gli screen reader
 * (torna visibile sotto i 700px, dove la tabella diventa una pila di schede).
 */
export default function ProcessField({
  field,
  value,
  invalid,
  readOnly,
  onChange,
  onUploadPhoto,
  compact = false,
}) {
  const id = `process-field-${field.key}`;
  const labelId = `${id}-label`;
  const errorId = `${id}-error`;
  const label = `${field.label || field.key}${field.required ? ' *' : ''}`;
  const set = (v) => onChange?.(field.key, v);

  // Una sezione non raccoglie nulla: è il titolo che spezza il form in blocchi.
  if (field.type === 'section') {
    return (
      <div className={styles.sectionHeading}>
        <h4>{field.label || field.key}</h4>
        {field.description && <p>{field.description}</p>}
      </div>
    );
  }

  const common = {
    id,
    name: field.key,
    disabled: readOnly,
    'aria-invalid': invalid || undefined,
    'aria-describedby': invalid ? errorId : undefined,
  };

  const control = renderControl({
    field,
    value,
    set,
    readOnly,
    common,
    id,
    labelId,
    onUploadPhoto,
  });

  return (
    <div
      className={styles.field}
      data-invalid={invalid ? 'true' : 'false'}
      data-compact={compact ? 'true' : 'false'}
    >
      {/* Checkbox e gruppo portano già la propria etichetta. */}
      {field.type !== 'checkbox' &&
        (LABELABLE.has(field.type) ? (
          <label id={labelId} htmlFor={id}>
            {label}
          </label>
        ) : (
          <span id={labelId} className={styles.fieldLabel}>
            {label}
          </span>
        ))}
      {control}
      {invalid && (
        <span id={errorId} className={styles.fieldError} role="alert">
          Campo obbligatorio
        </span>
      )}
    </div>
  );
}

function renderControl({ field, value, set, readOnly, common, id, labelId, onUploadPhoto }) {
  const options = (field.options || []).map((o) =>
    typeof o === 'string' ? { value: o, label: o } : o
  );

  switch (field.type) {
    case 'select':
      return (
        <AppSelect
          inputId={id}
          name={field.key}
          options={options}
          value={options.find((o) => o.value === value) || null}
          onChange={(opt) => set(opt?.value ?? '')}
          isDisabled={readOnly}
          isClearable={!field.required}
        />
      );

    case 'multiselect':
      return (
        <AppSelect
          inputId={id}
          name={field.key}
          isMulti
          options={options}
          value={options.filter((o) => (value || []).includes(o.value))}
          onChange={(opts) => set((opts || []).map((o) => o.value))}
          isDisabled={readOnly}
        />
      );

    // Scelta rapida: le opzioni brevi (OK / Usurato / Guasto) si leggono e si
    // toccano meglio come pulsanti che dentro una tendina.
    case 'radio':
      return (
        <div className={styles.radioGroup} role="radiogroup" aria-labelledby={labelId}>
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={value === o.value}
              className={styles.radioOption}
              data-selected={value === o.value}
              disabled={readOnly}
              onClick={() => set(value === o.value ? '' : o.value)}
            >
              {o.label}
            </button>
          ))}
        </div>
      );

    case 'checkbox':
      return (
        <AppCheckbox
          {...common}
          checked={value === true}
          onChange={(e) => set(e.target.checked)}
          label={`${field.label || field.key}${field.required ? ' *' : ''}`}
        />
      );

    case 'textarea':
      return (
        <textarea
          {...common}
          className="form-control"
          rows={3}
          value={value ?? ''}
          onChange={(e) => set(e.target.value)}
        />
      );

    case 'photo':
      return (
        <PhotoField
          field={field}
          photos={Array.isArray(value) ? value : []}
          readOnly={readOnly}
          onUpload={onUploadPhoto}
          onChange={set}
        />
      );

    case 'group':
      return (
        <GroupField
          field={field}
          value={value}
          readOnly={readOnly}
          onChange={set}
          labelId={labelId}
          onUploadPhoto={onUploadPhoto}
        />
      );

    default:
      return (
        <input
          {...common}
          className="form-control"
          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
          value={value ?? ''}
          onChange={(e) => set(e.target.value)}
        />
      );
  }
}

/**
 * Tabella ripetibile: un blocco di sotto-campi per riga.
 *
 * Le righe fisse (le quattro gomme) esistono già vuote e non si tolgono: sono
 * l'elenco da spuntare. Quelle libere si aggiungono e si rimuovono.
 *
 * Impaginata come una tabella e non come una pila di schede: i sotto-campi sono
 * gli stessi su ogni riga, quindi l'etichetta va scritta una volta in cima e le
 * colonne restano allineate — dieci voci di allestimento con tre etichette
 * ripetute ciascuna erano trenta ripetizioni da leggere.
 *
 * Le foto di una riga finiscono sotto `<gruppo>/<riga>`: due danni con lo stesso
 * `IMG_0001.jpg` non si sovrascrivono. `add_label` dà un nome al pulsante
 * ("Aggiungi danno") quando "riga" non dice nulla a chi compila.
 */
function GroupField({ field, value, readOnly, onChange, labelId, onUploadPhoto }) {
  const rows = groupRows(field, value);
  const subFields = field.fields || [];
  const hasRowLabels = rows.some((r) => r.fixed);
  const canRemove = !readOnly && rows.some((r) => !r.fixed);

  // La griglia deve essere la stessa su intestazione e righe, quindi il
  // template si calcola una volta qui: colonna dell'etichetta di riga (se
  // c'è), una per sotto-campo, e la colonna dell'azione quando serve.
  const template = [
    hasRowLabels ? 'minmax(120px, 200px)' : null,
    `repeat(${Math.max(subFields.length, 1)}, minmax(140px, 1fr))`,
    canRemove ? '40px' : null,
  ]
    .filter(Boolean)
    .join(' ');

  const writeRow = (rowKey, patch) => {
    const current = Array.isArray(value) ? value : [];
    const index = current.findIndex((r) => (r?._row || '') === rowKey);
    const next = [...current];
    if (index >= 0) next[index] = { ...next[index], ...patch };
    else next.push({ _row: rowKey, ...patch });
    onChange(next);
  };

  const addRow = () => {
    const current = Array.isArray(value) ? value : [];
    onChange([...current, { _row: `row_${Date.now()}` }]);
  };

  const removeRow = (rowKey) =>
    onChange((Array.isArray(value) ? value : []).filter((r) => (r?._row || '') !== rowKey));

  return (
    <div className={styles.group} aria-labelledby={labelId}>
      <div className={styles.groupTable}>
        {rows.length > 0 && (
          <div className={styles.groupHead} style={{ gridTemplateColumns: template }}>
            {hasRowLabels && <span />}
            {subFields.map((sub) => (
              <span key={sub.key}>{sub.label || sub.key}</span>
            ))}
            {canRemove && <span />}
          </div>
        )}

        {rows.map((row) => (
          <div key={row.key} className={styles.groupRow} style={{ gridTemplateColumns: template }}>
            {hasRowLabels && <span className={styles.groupRowLabel}>{row.label}</span>}
            {subFields.map((sub) => (
              <ProcessField
                key={sub.key}
                field={sub}
                value={row.values?.[sub.key]}
                readOnly={readOnly}
                compact
                onChange={(subKey, v) => writeRow(row.key, { [subKey]: v })}
                onUploadPhoto={
                  onUploadPhoto &&
                  ((subKey, files) => onUploadPhoto(`${field.key}/${row.key}/${subKey}`, files))
                }
              />
            ))}
            {canRemove && (
              <span className={styles.groupRowAction}>
                {!row.fixed && (
                  <AppButton
                    mode="icon"
                    variant="danger"
                    size="sm"
                    icon={ICONS.delete}
                    title="Rimuovi riga"
                    onClick={() => removeRow(row.key)}
                  />
                )}
              </span>
            )}
          </div>
        ))}
      </div>

      {!(field.rows || []).length && !readOnly && (
        <AppButton mode="nav" variant="neutral" size="sm" icon={ICONS.add} onClick={addRow}>
          {field.add_label || 'Aggiungi riga'}
        </AppButton>
      )}
    </div>
  );
}

/**
 * Foto della pratica. L'upload è iniettato dal chiamante (`onUploadPhoto`): il
 * componente generico non sa dove vada lo Storage, e senza handler resta in
 * sola lettura invece di offrire un pulsante che non funziona.
 */
function PhotoField({ field, photos, readOnly, onUpload, onChange }) {
  const canUpload = !readOnly && typeof onUpload === 'function';

  const handleFiles = async (e) => {
    const { files } = e.target;
    if (files?.length) {
      const uploaded = await onUpload(field.key, files);
      if (uploaded?.length) onChange([...photos, ...uploaded]);
    }
    e.target.value = '';
  };

  return (
    <div className={styles.photoField}>
      {photos.map((p) => (
        <a
          key={p.fullPath || p.url}
          href={p.url}
          target="_blank"
          rel="noreferrer"
          className={styles.photoThumb}
        >
          <img src={p.url} alt={p.name || field.label} loading="lazy" />
        </a>
      ))}

      {/* La casella tratteggiata resta anche a foto già scattate: il set è di
          più scatti per voce, non uno solo. */}
      {canUpload && (
        <label className={styles.photoUpload}>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={handleFiles}
          />
          <i className={ICONS.camera} aria-hidden="true" />
          <span>Aggiungi</span>
        </label>
      )}

      {!canUpload && photos.length === 0 && <span className={styles.photoEmpty}>—</span>}
    </div>
  );
}
